import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { SALayout } from "../../components/superadmin/SALayout";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

export default function SAAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setData(data));
  }, []);

  if (!data) return <SALayout><div className="text-[#A0A0A0]">Loading…</div></SALayout>;

  return (
    <SALayout>
      <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-6">Platform Analytics</h1>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card title="Platform Repeat Visit Rate % — last 12 months">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.months}>
              <CartesianGrid stroke="#ffffff10" />
              <XAxis dataKey="month" stroke="#A0A0A0" fontSize={11} />
              <YAxis stroke="#A0A0A0" fontSize={11} />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #9D00FF33" }} />
              <Line type="monotone" dataKey="repeat_rate" stroke="#9D00FF" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card title="New Customers & Active Tabs — last 12 months">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.months}>
              <CartesianGrid stroke="#ffffff10" />
              <XAxis dataKey="month" stroke="#A0A0A0" fontSize={11} />
              <YAxis stroke="#A0A0A0" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #9D00FF33" }} />
              <Bar dataKey="new_customers" fill="#9D00FF" />
              <Bar dataKey="active_tabs" fill="#D78CFF" />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex gap-3 text-[11px] text-[#A0A0A0] mt-3">
            <span><span className="inline-block w-2 h-2 bg-[#9D00FF] rounded-sm mr-1.5"></span>New Customers</span>
            <span><span className="inline-block w-2 h-2 bg-[#D78CFF] rounded-sm mr-1.5"></span>New Bottle Tabs</span>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card title="Top Bottle Brands — platform-wide">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.top_brands.slice(0, 10)} layout="vertical">
              <CartesianGrid stroke="#ffffff10" />
              <XAxis type="number" stroke="#A0A0A0" fontSize={11} />
              <YAxis type="category" dataKey="brand" stroke="#A0A0A0" fontSize={11} width={120} />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #9D00FF33" }} />
              <Bar dataKey="count" fill="#9D00FF" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Feature Adoption — % of active venues using each module (last 30d)" testid="sa-adoption-card">
          <div className="flex flex-col gap-3">
            {data.adoption.map((a) => (
              <div key={a.feature}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-white/90">{a.feature}</span>
                  <span className="text-white/90 font-bold">{a.pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-[#9D00FF]" style={{ width: `${Math.min(a.pct, 100)}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card title="Customer enrollment by source — platform-wide" className="mb-4">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-[#A0A0A0] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-right">Customers</th>
                <th className="px-3 py-2 text-right">% of total</th>
                <th className="px-3 py-2 text-right">Avg visits</th>
                <th className="px-3 py-2 text-right">Repeat rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(data.enrollment_by_source || []).length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-[#A0A0A0]">
                    No customer data yet.
                  </td>
                </tr>
              )}
              {(data.enrollment_by_source || []).map((s) => (
                <tr key={s.source} className="hover:bg-white/5">
                  <td className="px-3 py-2 text-white/90 font-medium">{s.label}</td>
                  <td className="px-3 py-2 text-white/90 text-right">
                    {s.count.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[#9D00FF]"
                          style={{ width: `${Math.min(s.pct, 100)}%` }}
                        />
                      </div>
                      <span className="text-white/90 w-10 text-right">{s.pct}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-white/90 text-right">{s.avg_visits}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`font-semibold ${
                      s.repeat_rate >= 40 ? "text-emerald-400" :
                      s.repeat_rate >= 20 ? "text-amber-400" :
                      "text-[#A0A0A0]"
                    }`}>
                      {s.repeat_rate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Geography — by City">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="sa-geo-table">
            <thead className="text-[#A0A0A0] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">City</th>
                <th className="px-3 py-2 text-left">Venues</th>
                <th className="px-3 py-2 text-left">Customers</th>
                <th className="px-3 py-2 text-left">Active Tabs</th>
                <th className="px-3 py-2 text-left">Repeat Rate %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {data.geography.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-[#A0A0A0]">No venues yet.</td></tr>}
              {data.geography.map((g) => (
                <tr key={g.city} className="hover:bg-white/5">
                  <td className="px-3 py-2 text-white/90">{g.city}</td>
                  <td className="px-3 py-2 text-white/90">{g.venues}</td>
                  <td className="px-3 py-2 text-white/90">{g.customers}</td>
                  <td className="px-3 py-2 text-white/90">{g.active_tabs}</td>
                  <td className="px-3 py-2 text-white/90">{g.repeat_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-6 flex flex-wrap items-center gap-6 text-xs text-[#A0A0A0]">
        <span>Avg bottle tabs per venue this month: <span className="text-white font-bold">{data.avg_tabs_per_venue}</span></span>
        {data.otp_verification_rate !== null && data.otp_verification_rate !== undefined ? (
          <span>
            OTP Verification Rate:{" "}
            <span className={`font-bold ${data.otp_verification_rate >= 80 ? "text-emerald-400" : data.otp_verification_rate >= 50 ? "text-amber-400" : "text-red-400"}`}>
              {data.otp_verification_rate}%
            </span>{" "}
            <span className="text-[#A0A0A0]">({data.otp_verified_count} verified / {data.otp_bypassed_count} overrides)</span>
          </span>
        ) : (
          <span>OTP Verification Rate: <span className="text-white font-bold">—</span> <span className="text-[#A0A0A0]">(no OTP actions yet)</span></span>
        )}
      </div>
    </SALayout>
  );
}

function Card({ title, children, className = "", testid }) {
  return (
    <div className={`bg-[#1A1A1A] border border-white/10 rounded-xl p-5 ${className}`} data-testid={testid}>
      <div className="text-sm font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}
